import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { useOutletContext } from 'react-router-dom';
import { sb } from '../lib/supabase';
import { useSupabaseAuth } from '../context/AuthContext';
import useWorkspaceAccess from '../hooks/useWorkspaceAccess';
import Loader from '../components/Loader';
import Modal from '../components/Modal';
import OwnerSearch from '../components/OwnerSearch';

const STATUS_COLORS = { planning: '#7B8FD4', active: '#534AB7', review: '#EF9F27', blocked: '#E24B4A', complete: '#1D9E75', custom: '#888780' };
const STATUS_LABEL = { planning: 'Planning', active: 'Active', review: 'Review', blocked: 'Blocked', complete: 'Complete', custom: 'Custom' };
const MS_COLORS = { milestone: '#EF9F27', release: '#534AB7', go_live: '#1D9E75' };
const MS_LABEL = { milestone: 'Milestone', release: 'Release', go_live: 'Go-live' };
const LANE_PALETTE = ['#534AB7', '#1D9E75', '#EF9F27', '#E24B4A', '#7B8FD4', '#0C447C', '#854F0B', '#A32D2D'];
const ZOOMS = [
  { id: 'day', label: 'Day', px: 30 }, { id: 'week', label: 'Week', px: 14 },
  { id: 'biweek', label: '2-Wk', px: 8 }, { id: 'month', label: 'Month', px: 4.2 },
  { id: 'quarter', label: 'Qtr', px: 2 }, { id: 'half', label: '6-Mo', px: 1.1 },
];
const ROW_SIZES = { S: { row: 30, bar: 14, ms: 10 }, M: { row: 42, bar: 22, ms: 13 }, L: { row: 56, bar: 30, ms: 18 } };

const DAY = 86400000;
const toDate = (s) => (s ? new Date(s + 'T00:00:00') : null);
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const dayDiff = (a, b) => Math.round((a - b) / DAY);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const shortDate = (d) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;
function workingDays(a, b) {
  let n = 0; const cur = new Date(a);
  while (cur <= b) { const wd = cur.getDay(); if (wd !== 0 && wd !== 6) n++; cur.setDate(cur.getDate() + 1); }
  return n;
}

export default function Plan() {
  const { projects, project } = useOutletContext();
  const { profile } = useSupabaseAuth();
  const { canWrite } = useWorkspaceAccess();

  const defaultProjectId = project?.id || projects?.[0]?.id || '';
  const [projectId, setProjectId] = useState(defaultProjectId);
  const [lanes, setLanes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState('gantt');
  const [zoom, setZoom] = useState('week');
  const [size, setSize] = useState('M');
  const [rotLabels, setRotLabels] = useState(true);
  const [modal, setModal] = useState(null);

  const scrollRef = useRef(null);
  const px = ZOOMS.find((z) => z.id === zoom).px;
  const dim = ROW_SIZES[size];

  const load = useCallback(async () => {
    if (!projectId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [lRes, tRes, mRes] = await Promise.all([
        sb.from('plan_swimlanes').select('*').eq('project_id', projectId).order('order_index'),
        sb.from('plan_tasks').select('*, owner:profiles!plan_tasks_owner_id_fkey(full_name)').eq('project_id', projectId).order('order_index'),
        sb.from('plan_milestones').select('*').eq('project_id', projectId).order('order_index'),
      ]);
      setLanes(lRes.data || []);
      setTasks(tRes.data || []);
      setMilestones(mRes.data || []);
    } catch (e) {
      console.error('[Plan] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (project?.id) setProjectId(project.id);
  }, [project?.id]);

  // Unified items per lane
  const itemsByLane = useMemo(() => {
    const m = {};
    lanes.forEach((l) => { m[l.id] = []; });
    tasks.forEach((t) => { (m[t.lane_id] ||= []).push({ ...t, _kind: 'task' }); });
    milestones.forEach((mi) => { (m[mi.lane_id] ||= []).push({ ...mi, _kind: 'ms' }); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.order_index - b.order_index) || ((a.start_date || a.milestone_date) > (b.start_date || b.milestone_date) ? 1 : -1)));
    return m;
  }, [lanes, tasks, milestones]);

  // Date range
  const range = useMemo(() => {
    const dates = [];
    tasks.forEach((t) => { if (t.start_date) dates.push(toDate(t.start_date)); if (t.end_date) dates.push(toDate(t.end_date)); });
    milestones.forEach((mi) => { if (mi.milestone_date) dates.push(toDate(mi.milestone_date)); });
    let min, max;
    if (dates.length) { min = new Date(Math.min(...dates)); max = new Date(Math.max(...dates)); }
    else { min = addDays(new Date(), -30); max = addDays(new Date(), 45); }
    min = addDays(min, -7); max = addDays(max, 14);
    return { min, max, days: dayDiff(max, min) + 1 };
  }, [tasks, milestones]);

  const xOf = useCallback((d) => dayDiff(toDate(typeof d === 'string' ? d : ymd(d)), range.min) * px, [range.min, px]);
  const canvasW = range.days * px;

  if (!projectId) return <div className="content"><div className="empty" style={{ marginTop: 40 }}>This programme has no projects yet. Create a project to start planning.</div></div>;
  if (loading) return <div className="content"><Loader text="Loading plan…" /></div>;

  return (
    <div className="content">
      <PlanToolbar
        projects={projects} projectId={projectId} setProjectId={setProjectId}
        showProjectPicker={!project && projects.length > 1}
        view={view} setView={setView} zoom={zoom} setZoom={setZoom} size={size} setSize={setSize}
        rotLabels={rotLabels} setRotLabels={setRotLabels} canWrite={canWrite}
        onToday={() => { if (scrollRef.current) scrollRef.current.scrollLeft = Math.max(0, dayDiff(new Date(), range.min) * px - 300); }}
        onAddTask={() => setModal({ kind: 'task', mode: 'add' })}
        onAddMs={() => setModal({ kind: 'ms', mode: 'add' })}
        onLanes={() => setModal({ kind: 'lanes' })}
        onExport={() => exportCsv(lanes, tasks, milestones)}
        onImport={() => setModal({ kind: 'import' })}
      />

      {lanes.length === 0 ? (
        <div className="empty" style={{ marginTop: 24 }}>
          No swimlanes yet.{canWrite ? <> <span style={{ color: '#534AB7', cursor: 'pointer' }} onClick={() => setModal({ kind: 'lanes' })}>Add a swimlane</span> to start adding tasks and milestones.</> : ' View-only access.'}
        </div>
      ) : view === 'gantt' ? (
        <GanttView
          lanes={lanes} itemsByLane={itemsByLane} range={range} px={px} dim={dim} canvasW={canvasW} xOf={xOf}
          rotLabels={rotLabels} scrollRef={scrollRef} canWrite={canWrite}
          onEdit={(item) => setModal({ kind: item._kind, mode: 'edit', item })}
          onCommit={commitDates}
        />
      ) : (
        <GridView lanes={lanes} itemsByLane={itemsByLane} canWrite={canWrite} onEdit={(item) => setModal({ kind: item._kind, mode: 'edit', item })} onInline={inlineUpdate} />
      )}

      {/* Modals */}
      {(modal?.kind === 'task' || modal?.kind === 'ms') && (
        <ItemModal modal={modal} kind={modal.kind} lanes={lanes} projectId={projectId} profile={profile}
          itemsByLane={itemsByLane}
          onClose={() => setModal(null)} onSaved={async () => { setModal(null); await load(); }}
          onDeleted={async () => { setModal(null); await load(); }} />
      )}
      {modal?.kind === 'lanes' && (
        <LanesModal lanes={lanes} tasks={tasks} milestones={milestones} projectId={projectId} profile={profile}
          onClose={() => setModal(null)} onSaved={async () => { await load(); }} />
      )}
      {modal?.kind === 'import' && (
        <ImportModal lanes={lanes} projectId={projectId} profile={profile} onClose={() => setModal(null)} onSaved={async () => { setModal(null); await load(); }} />
      )}
    </div>
  );

  // commit drag changes to DB + local state
  async function commitDates(item, patch) {
    if (item._kind === 'task') {
      setTasks((prev) => prev.map((t) => (t.id === item.id ? { ...t, ...patch } : t)));
      await sb.from('plan_tasks').update(patch).eq('id', item.id);
    } else {
      setMilestones((prev) => prev.map((m) => (m.id === item.id ? { ...m, ...patch } : m)));
      await sb.from('plan_milestones').update(patch).eq('id', item.id);
    }
  }

  async function inlineUpdate(item, patch) {
    if (item._kind === 'task') {
      setTasks((prev) => prev.map((t) => (t.id === item.id ? { ...t, ...patch } : t)));
      await sb.from('plan_tasks').update(patch).eq('id', item.id);
    } else {
      setMilestones((prev) => prev.map((m) => (m.id === item.id ? { ...m, ...patch } : m)));
      await sb.from('plan_milestones').update(patch).eq('id', item.id);
    }
  }
}

// ── Toolbar ──────────────────────────────────────────────────────────────────
function PlanToolbar({ projects, projectId, setProjectId, showProjectPicker, view, setView, zoom, setZoom, size, setSize, rotLabels, setRotLabels, canWrite, onToday, onAddTask, onAddMs, onLanes, onExport, onImport }) {
  return (
    <div className="plan-toolbar">
      {showProjectPicker && (
        <select className="form-select" style={{ width: 'auto' }} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      )}
      <div className="plan-seg">
        <button className={view === 'gantt' ? 'on' : ''} onClick={() => setView('gantt')}>Gantt</button>
        <button className={view === 'grid' ? 'on' : ''} onClick={() => setView('grid')}>Grid</button>
      </div>
      {view === 'gantt' && (
        <>
          <div className="plan-seg">
            {ZOOMS.map((z) => <button key={z.id} className={zoom === z.id ? 'on' : ''} onClick={() => setZoom(z.id)}>{z.label}</button>)}
          </div>
          <div className="plan-seg">
            {['S', 'M', 'L'].map((s) => <button key={s} className={size === s ? 'on' : ''} onClick={() => setSize(s)}>{s}</button>)}
          </div>
          <button className="btn-ghost" onClick={() => setRotLabels((v) => !v)}>{rotLabels ? 'Lane: vertical' : 'Lane: horizontal'}</button>
          <button className="btn-ghost" onClick={onToday}>Today</button>
        </>
      )}
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
        {canWrite && (
          <>
            <button className="btn-ghost" onClick={onLanes}>⊞ Swimlanes</button>
            <button className="btn-ghost" onClick={onImport}>Import</button>
          </>
        )}
        <button className="btn-ghost" onClick={onExport}>Export</button>
        {canWrite && (
          <>
            <button className="btn-ghost" onClick={onAddMs}>◆ Milestone</button>
            <button className="btn-primary" onClick={onAddTask}>+ Task</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Gantt view ───────────────────────────────────────────────────────────────
function GanttView({ lanes, itemsByLane, range, px, dim, canvasW, xOf, rotLabels, scrollRef, canWrite, onEdit, onCommit }) {
  const [drag, setDrag] = useState(null); // {item, mode, preview}
  const dragRef = useRef(null);

  // header month cells
  const monthCells = [];
  let cur = new Date(range.min.getFullYear(), range.min.getMonth(), 1);
  while (cur <= range.max) {
    const monthStart = cur < range.min ? range.min : cur;
    const next = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    const monthEnd = next > range.max ? range.max : addDays(next, -1);
    const left = dayDiff(monthStart, range.min) * px;
    const width = (dayDiff(monthEnd, monthStart) + 1) * px;
    monthCells.push({ key: ymd(cur), left, width, label: `${MONTHS[cur.getMonth()]} ${cur.getFullYear()}` });
    cur = next;
  }
  // band2 cells: days (when wide) else weeks (Mondays)
  const sub = [];
  if (px >= 18) {
    for (let i = 0; i < range.days; i++) { const d = addDays(range.min, i); sub.push({ key: i, left: i * px, width: px, label: String(d.getDate()) }); }
  } else {
    let w = new Date(range.min); const off = (w.getDay() + 6) % 7; w = addDays(w, -off);
    while (w <= range.max) { const left = dayDiff(w, range.min) * px; sub.push({ key: ymd(w), left, width: 7 * px, label: shortDate(w) }); w = addDays(w, 7); }
  }

  function startDrag(e, item, mode) {
    if (!canWrite) return;
    e.preventDefault(); e.stopPropagation();
    dragRef.current = { item, mode, startX: e.clientX, moved: false };
    const onMove = (ev) => {
      const info = dragRef.current; if (!info) return;
      const delta = Math.round((ev.clientX - info.startX) / px);
      if (Math.abs(ev.clientX - info.startX) > 4) info.moved = true;
      let preview = {};
      if (item._kind === 'task') {
        const s = toDate(item.start_date), en = toDate(item.end_date);
        if (info.mode === 'move') preview = { start_date: ymd(addDays(s, delta)), end_date: ymd(addDays(en, delta)) };
        else { const ne = addDays(en, delta); if (ne >= s) preview = { end_date: ymd(ne) }; }
      } else {
        preview = { milestone_date: ymd(addDays(toDate(item.milestone_date), delta)) };
      }
      setDrag({ id: item.id, preview });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const info = dragRef.current; dragRef.current = null;
      setDrag((d) => {
        if (info && info.moved && d?.preview && Object.keys(d.preview).length) onCommit(item, d.preview);
        else if (info && !info.moved) onEdit(item);
        return null;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  const rowsPerLane = lanes.map((l) => ({ lane: l, items: itemsByLane[l.id] || [] }));

  return (
    <div className="gantt-wrap">
      {/* Lane label column */}
      <div className="lane-col">
        <div style={{ height: 46, borderBottom: '1px solid #d3d1c7', background: '#faf9f6' }} />
        {rowsPerLane.map(({ lane, items }) => {
          const h = Math.max(items.length, 1) * dim.row;
          return (
            <div key={lane.id} className="lane-seg" style={{ height: h }}>
              <div className="lane-strip" style={{ background: lane.color || '#534AB7' }} />
              <div className={`lane-name${rotLabels ? ' rot' : ''}`} style={{ color: lane.color || '#534AB7' }}>{lane.name}</div>
            </div>
          );
        })}
      </div>

      {/* Timeline */}
      <div className="gantt-scroll" ref={scrollRef}>
        <div className="gantt-canvas" style={{ width: canvasW }}>
          <div className="gantt-hdr">
            <div className="gantt-band">
              {monthCells.map((c) => <div key={c.key} className="gantt-cell" style={{ position: 'absolute', left: c.left, width: c.width }}>{c.label}</div>)}
            </div>
            <div className="gantt-band b2">
              {sub.map((c) => <div key={c.key} className="gantt-cell b2" style={{ position: 'absolute', left: c.left, width: c.width }}>{c.label}</div>)}
            </div>
          </div>

          <div className="gantt-body">
            {/* today line */}
            {(() => { const t = new Date(); if (t >= range.min && t <= range.max) return <div className="today-line" style={{ left: dayDiff(t, range.min) * px }} />; return null; })()}

            {rowsPerLane.map(({ lane, items }) => {
              const rows = items.length ? items : [null];
              return rows.map((item, ri) => (
                <div key={item ? item.id : `${lane.id}-empty`} className={`grow${ri === rows.length - 1 ? ' lane-end' : ''}`} style={{ height: dim.row }}>
                  {item && item._kind === 'task' && (
                    <TaskBar item={item} px={px} dim={dim} xOf={xOf} range={range} drag={drag} onStart={startDrag} />
                  )}
                  {item && item._kind === 'ms' && (
                    <MsDiamond item={item} px={px} dim={dim} xOf={xOf} range={range} drag={drag} onStart={startDrag} />
                  )}
                </div>
              ));
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function TaskBar({ item, px, dim, range, drag, onStart }) {
  const ov = drag?.id === item.id ? drag.preview : null;
  const start = toDate(ov?.start_date || item.start_date);
  const end = toDate(ov?.end_date || item.end_date);
  const left = dayDiff(start, range.min) * px;
  const width = Math.max((dayDiff(end, start) + 1) * px, 6);
  const color = item.status === 'custom' && item.color ? item.color : STATUS_COLORS[item.status] || STATUS_COLORS.planning;
  const progress = Math.max(0, Math.min(100, item.progress || 0));
  return (
    <>
      <div className={`gbar${drag?.id === item.id ? ' dragging' : ''}`} style={{ left, width, height: dim.bar, background: color }}
        onMouseDown={(e) => onStart(e, item, 'move')} title={`${item.name} · ${shortDate(start)} → ${shortDate(end)} · ${workingDays(start, end)}d`}>
        {progress < 100 && <div className="gbar-fill" style={{ left: `${progress}%` }} />}
        <div className="gbar-resize" onMouseDown={(e) => onStart(e, item, 'resize')} />
      </div>
      <div className="gbar-label" style={{ left: left + width + 6 }}>{item.name}</div>
    </>
  );
}

function MsDiamond({ item, px, dim, range, drag, onStart }) {
  const ov = drag?.id === item.id ? drag.preview : null;
  const date = toDate(ov?.milestone_date || item.milestone_date);
  const left = dayDiff(date, range.min) * px;
  const color = item.color || MS_COLORS[item.milestone_type] || MS_COLORS.milestone;
  return (
    <div className="gms" style={{ left: left - dim.ms / 2, transform: 'translateY(-50%)' }} onMouseDown={(e) => onStart(e, item, 'move')}>
      <div className="gms-diamond" style={{ width: dim.ms, height: dim.ms, background: color }} />
      <div className="gms-label">{item.name}</div>
    </div>
  );
}

// ── Grid view ────────────────────────────────────────────────────────────────
function GridView({ lanes, itemsByLane, canWrite, onEdit, onInline }) {
  return (
    <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
      <table className="plan-grid-tbl">
        <thead>
          <tr>
            <th style={{ width: '30%' }}>Name</th><th>Type</th><th>Start</th><th>End / date</th><th style={{ width: 90 }}>Progress</th><th>Status / type</th><th>Owner</th>{canWrite && <th />}
          </tr>
        </thead>
        <tbody>
          {lanes.map((lane) => {
            const items = itemsByLane[lane.id] || [];
            return (
              <Fragment key={lane.id}>
                <tr className="plan-lane-hdr">
                  <td colSpan={canWrite ? 8 : 7} style={{ color: lane.color || '#534AB7' }}>▌ {lane.name}</td>
                </tr>
                {items.length === 0 ? (
                  <tr key={`empty-${lane.id}`}><td colSpan={canWrite ? 8 : 7} style={{ color: '#888780', fontStyle: 'italic' }}>No items</td></tr>
                ) : items.map((item) => item._kind === 'task' ? (
                  <tr key={item.id}>
                    <td>{canWrite
                      ? <input defaultValue={item.name} onBlur={(e) => e.target.value !== item.name && onInline(item, { name: e.target.value })} />
                      : item.name}</td>
                    <td style={{ color: '#888780' }}>Task</td>
                    <td>{canWrite
                      ? <input type="date" defaultValue={item.start_date} onBlur={(e) => e.target.value !== item.start_date && onInline(item, { start_date: e.target.value })} />
                      : item.start_date}</td>
                    <td>{canWrite
                      ? <input type="date" defaultValue={item.end_date} onBlur={(e) => e.target.value !== item.end_date && onInline(item, { end_date: e.target.value })} />
                      : item.end_date}</td>
                    <td>{canWrite
                      ? <input type="number" min="0" max="100" defaultValue={item.progress} onBlur={(e) => Number(e.target.value) !== item.progress && onInline(item, { progress: Number(e.target.value) })} />
                      : `${item.progress ?? 0}%`}</td>
                    <td>{canWrite ? (
                      <select defaultValue={item.status} onChange={(e) => onInline(item, { status: e.target.value })}>
                        {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                      </select>
                    ) : (STATUS_LABEL[item.status] || item.status)}</td>
                    <td style={{ color: '#5f5e5a' }}>{item.owner?.full_name || '—'}</td>
                    {canWrite && <td><button className="row-act" onClick={() => onEdit(item)}>Edit</button></td>}
                  </tr>
                ) : (
                  <tr key={item.id}>
                    <td>{canWrite
                      ? <input defaultValue={item.name} onBlur={(e) => e.target.value !== item.name && onInline(item, { name: e.target.value })} />
                      : item.name}</td>
                    <td style={{ color: '#888780' }}>◆ Milestone</td>
                    <td style={{ color: '#d3d1c7' }}>—</td>
                    <td>{canWrite
                      ? <input type="date" defaultValue={item.milestone_date} onBlur={(e) => e.target.value !== item.milestone_date && onInline(item, { milestone_date: e.target.value })} />
                      : item.milestone_date}</td>
                    <td style={{ color: '#d3d1c7' }}>—</td>
                    <td>{canWrite ? (
                      <select defaultValue={item.milestone_type} onChange={(e) => onInline(item, { milestone_type: e.target.value })}>
                        {Object.keys(MS_LABEL).map((s) => <option key={s} value={s}>{MS_LABEL[s]}</option>)}
                      </select>
                    ) : (MS_LABEL[item.milestone_type] || item.milestone_type)}</td>
                    <td style={{ color: '#d3d1c7' }}>—</td>
                    {canWrite && <td><button className="row-act" onClick={() => onEdit(item)}>Edit</button></td>}
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Item modal (task / milestone, add + edit + delete) ──────────────────────────
function ItemModal({ modal, kind, lanes, projectId, profile, itemsByLane, onClose, onSaved, onDeleted }) {
  const editing = modal.mode === 'edit' ? modal.item : null;
  const isTask = kind === 'task';
  const today = ymd(new Date());
  const [name, setName] = useState(editing?.name || '');
  const [laneId, setLaneId] = useState(editing?.lane_id || lanes[0]?.id || '');
  const [start, setStart] = useState(editing?.start_date || today);
  const [end, setEnd] = useState(editing?.end_date || ymd(addDays(new Date(), 5)));
  const [progress, setProgress] = useState(editing?.progress ?? 0);
  const [status, setStatus] = useState(editing?.status || 'planning');
  const [ownerId, setOwnerId] = useState(editing?.owner_id || null);
  const [ownerName, setOwnerName] = useState(editing?.owner?.full_name || '');
  const [msDate, setMsDate] = useState(editing?.milestone_date || today);
  const [msType, setMsType] = useState(editing?.milestone_type || 'milestone');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    setSaving(true); setError(null);
    let res;
    if (isTask) {
      const payload = { name: name.trim(), lane_id: laneId, start_date: start, end_date: end, progress: Number(progress) || 0, status, owner_id: ownerId || null };
      if (editing) res = await sb.from('plan_tasks').update(payload).eq('id', editing.id).select('id');
      else res = await sb.from('plan_tasks').insert({ ...payload, project_id: projectId, order_index: (itemsByLane[laneId]?.length || 0) + 1, created_by: profile?.id || null }).select('id');
    } else {
      const payload = { name: name.trim(), lane_id: laneId, milestone_date: msDate, milestone_type: msType };
      if (editing) res = await sb.from('plan_milestones').update(payload).eq('id', editing.id).select('id');
      else res = await sb.from('plan_milestones').insert({ ...payload, project_id: projectId, order_index: (itemsByLane[laneId]?.length || 0) + 1, created_by: profile?.id || null }).select('id');
    }
    if (res.error) { setSaving(false); setError(res.error.message); return; }
    setSaving(false); onSaved();
  }

  async function del() {
    if (!editing) return;
    setSaving(true);
    await sb.from(isTask ? 'plan_tasks' : 'plan_milestones').delete().eq('id', editing.id);
    setSaving(false); onDeleted();
  }

  const title = `${editing ? 'Edit' : 'Add'} ${isTask ? 'task' : 'milestone'}`;
  return (
    <Modal title={title} onClose={onClose} busy={saving}
      footer={<>
        {editing && <button className="btn-ghost" style={{ color: '#A32D2D', marginRight: 'auto' }} onClick={del} disabled={saving}>Delete</button>}
        <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn-primary" onClick={save} disabled={saving || !name.trim() || !laneId}>{saving ? 'Saving…' : editing ? 'Save' : 'Add'}</button>
      </>}>
      <div className="form-row"><label className="form-label">Name *</label><input className="form-input" value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div className="form-row"><label className="form-label">Swimlane</label>
        <select className="form-select" value={laneId} onChange={(e) => setLaneId(e.target.value)}>
          {lanes.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>
      {isTask ? (
        <>
          <div className="form-2col">
            <div className="form-row"><label className="form-label">Start</label><input className="form-input" type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
            <div className="form-row"><label className="form-label">End</label><input className="form-input" type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          </div>
          <div className="form-2col">
            <div className="form-row"><label className="form-label">Progress %</label><input className="form-input" type="number" min="0" max="100" value={progress} onChange={(e) => setProgress(e.target.value)} /></div>
            <div className="form-row"><label className="form-label">Status</label>
              <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row"><label className="form-label">Owner</label><OwnerSearch displayName={ownerName} onChange={(id, n) => { setOwnerId(id); setOwnerName(n); }} /></div>
        </>
      ) : (
        <div className="form-2col">
          <div className="form-row"><label className="form-label">Date</label><input className="form-input" type="date" value={msDate} onChange={(e) => setMsDate(e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Type</label>
            <select className="form-select" value={msType} onChange={(e) => setMsType(e.target.value)}>
              {Object.keys(MS_LABEL).map((s) => <option key={s} value={s}>{MS_LABEL[s]}</option>)}
            </select>
          </div>
        </div>
      )}
      {error && <div className="form-error" style={{ margin: 0 }}>{error}</div>}
    </Modal>
  );
}

// ── Swimlanes manager ──────────────────────────────────────────────────────────
function LanesModal({ lanes, tasks, milestones, projectId, profile, onClose, onSaved }) {
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  async function add() {
    if (!newName.trim()) return;
    setBusy(true);
    const color = LANE_PALETTE[lanes.length % LANE_PALETTE.length];
    await sb.from('plan_swimlanes').insert({ project_id: projectId, name: newName.trim(), color, order_index: lanes.length + 1, created_by: profile?.id || null });
    setNewName(''); setBusy(false); onSaved();
  }
  async function recolor(lane, color) { await sb.from('plan_swimlanes').update({ color }).eq('id', lane.id); onSaved(); }
  async function remove(lane) {
    const others = lanes.filter((l) => l.id !== lane.id);
    if (others.length) {
      const target = others[0].id;
      await Promise.all([
        sb.from('plan_tasks').update({ lane_id: target }).eq('lane_id', lane.id),
        sb.from('plan_milestones').update({ lane_id: target }).eq('lane_id', lane.id),
      ]);
    }
    await sb.from('plan_swimlanes').delete().eq('id', lane.id);
    onSaved();
  }

  return (
    <Modal title="Swimlanes" onClose={onClose} footer={<button className="btn-primary" onClick={onClose}>Done</button>}>
      {lanes.map((lane) => {
        const count = tasks.filter((t) => t.lane_id === lane.id).length + milestones.filter((m) => m.lane_id === lane.id).length;
        return (
          <div key={lane.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '0.5px solid #f1efe8' }}>
            <input type="color" value={lane.color || '#534AB7'} onChange={(e) => recolor(lane, e.target.value)} style={{ width: 26, height: 26, border: 'none', background: 'none', cursor: 'pointer' }} />
            <span style={{ flex: 1, fontSize: 12, color: '#2c2c2a' }}>{lane.name}</span>
            <span style={{ fontSize: 10, color: '#888780' }}>{count} item{count !== 1 ? 's' : ''}</span>
            <button className="row-act row-act-close" onClick={() => remove(lane)} disabled={lanes.length === 1} title={lanes.length === 1 ? 'At least one lane required' : 'Remove'}>✕</button>
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <input className="form-input" placeholder="New swimlane name" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
        <button className="btn-primary" onClick={add} disabled={busy || !newName.trim()}>Add</button>
      </div>
    </Modal>
  );
}

// ── CSV import ───────────────────────────────────────────────────────────────
function ImportModal({ lanes, projectId, profile, onClose, onSaved }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  function onFile(e) {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ''));
    reader.readAsText(f);
  }

  async function run() {
    setBusy(true); setError(null);
    try {
      const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
      if (lines[0]?.toLowerCase().startsWith('type,')) lines.shift();
      const laneByName = {}; lanes.forEach((l) => { laneByName[l.name.toLowerCase()] = l.id; });
      let order = 1000;
      for (const line of lines) {
        const [type, name, swimlane, start, end, progress, status] = line.split(',').map((s) => s.trim());
        if (!name) continue;
        let laneId = laneByName[(swimlane || '').toLowerCase()];
        if (!laneId) {
          const color = LANE_PALETTE[Object.keys(laneByName).length % LANE_PALETTE.length];
          const { data } = await sb.from('plan_swimlanes').insert({ project_id: projectId, name: swimlane || 'Imported', color, order_index: 99, created_by: profile?.id || null }).select('id, name');
          laneId = data?.[0]?.id; if (data?.[0]) laneByName[data[0].name.toLowerCase()] = laneId;
        }
        if ((type || 'task').toLowerCase().startsWith('m') && type?.toLowerCase() !== 'task') {
          await sb.from('plan_milestones').insert({ project_id: projectId, lane_id: laneId, name, milestone_date: start || end || ymd(new Date()), milestone_type: 'milestone', order_index: order++ });
        } else {
          await sb.from('plan_tasks').insert({ project_id: projectId, lane_id: laneId, name, start_date: start || ymd(new Date()), end_date: end || start || ymd(addDays(new Date(), 3)), progress: Number(progress) || 0, status: status || 'planning', order_index: order++, created_by: profile?.id || null });
        }
      }
      setBusy(false); onSaved();
    } catch (e) { setBusy(false); setError(e.message); }
  }

  return (
    <Modal title="Import from CSV" onClose={onClose} busy={busy}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={run} disabled={busy || !text.trim()}>Import</button></>}>
      <div className="form-row"><label className="form-label">Upload CSV</label><input type="file" accept=".csv,text/csv" onChange={onFile} /></div>
      <div className="form-row"><label className="form-label">…or paste CSV</label>
        <textarea className="form-textarea" rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder={'type,name,swimlane,start,end,progress,status,owner\ntask,Design API,Build,2026-06-01,2026-06-10,20,active,'} />
      </div>
      <div style={{ fontSize: 11, color: '#888780' }}>Columns: type, name, swimlane, start, end, progress, status, owner. Swimlanes are matched by name (created if missing). Simple comma split — avoid commas inside fields.</div>
      {error && <div className="form-error" style={{ margin: 0 }}>{error}</div>}
    </Modal>
  );
}

// ── CSV export ───────────────────────────────────────────────────────────────
function exportCsv(lanes, tasks, milestones) {
  const laneName = (id) => lanes.find((l) => l.id === id)?.name || '';
  const rows = [['type', 'name', 'swimlane', 'start', 'end', 'progress', 'status', 'owner']];
  tasks.forEach((t) => rows.push(['task', t.name, laneName(t.lane_id), t.start_date || '', t.end_date || '', t.progress ?? '', t.status || '', t.owner?.full_name || '']));
  milestones.forEach((m) => rows.push(['milestone', m.name, laneName(m.lane_id), m.milestone_date || '', '', '', m.milestone_type || '', '']));
  const csv = rows.map((r) => r.map((c) => `${c}`.replace(/,/g, ';')).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'plan.csv'; a.click();
  URL.revokeObjectURL(url);
}
